'use client';
import { useResource } from '@/lib/use-resource';

interface AmBrief {
  date: string | null;
  portfolioHoldings?: number;
  priorSnapshot?: string | null;
  earningsDay?: string;
  earningsCount?: number;
  pdfFile: string | null;
  generatedAt?: string;
  recent?: { date: string; pdfFile: string }[];
}

export function AmBriefCard() {
  const { data: brief, loading } = useResource<AmBrief>('/api/am-report');

  if (loading) return <div className="text-[12px] text-[#64748b]">Loading AM brief…</div>;
  if (!brief?.date || !brief.pdfFile) {
    return (
      <div className="text-[11px] text-[#64748b]">
        No morning brief yet. The 6 AM daily-refresh produces this.
      </div>
    );
  }

  const priors = (brief.recent ?? []).filter(r => r.date !== brief.date).slice(0, 4);

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] text-[#cbd5e1]">
            AM Brief for <span className="font-mono text-amber-300">{brief.date}</span>
            {brief.priorSnapshot && (
              <span className="text-[#64748b]"> · vs {brief.priorSnapshot}</span>
            )}
          </div>
          <div className="text-[10px] text-[#94a3b8] mt-0.5">
            {brief.portfolioHoldings} holdings covered · {brief.earningsCount} reported on {brief.earningsDay}
          </div>
        </div>
        <a
          href={`/api/am-report/pdf?date=${brief.date}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] px-3 py-1.5 rounded border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 hover:border-amber-400/70 transition-colors"
        >
          Open PDF →
        </a>
      </div>

      {priors.length > 0 && (
        <div className="border-t border-white/5 pt-2">
          <div className="text-[10px] uppercase tracking-wider text-[#64748b] mb-1.5">Prior briefs</div>
          <ul className="space-y-1">
            {priors.map(p => (
              <li key={p.date} className="flex items-center justify-between gap-3 text-[11px]">
                <span className="font-mono text-[#94a3b8]">{p.date}</span>
                <a
                  href={`/api/am-report/pdf?date=${p.date}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-300/70 hover:text-amber-300 transition-colors"
                >
                  Open PDF →
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
